import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import type { OnChanges, OnDestroy, OnInit } from '@angular/core';
import { KeyboardService } from '@freelancer/keyboard';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { FlexAlign, FlexJustify } from '@freelancer/ui/flex';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import { isDefined } from '@freelancer/utils';
import type { Observable, Subscription } from 'rxjs';
import { fromEvent, merge, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  startWith,
  throttleTime,
} from 'rxjs/operators';
import {
  StickyFooterBackgroundColor,
  StickyFooterPosition,
} from './sticky-footer.types';
import { StickyContext } from '../sticky/sticky.interface';
import { StickyModule } from '../sticky/sticky.module';

// Keeps the footer tracking the keyboard without running a layout read on every
// viewport event.
const KEYBOARD_INSET_THROTTLE_MS = 50;
const THROTTLE_BOTH = { leading: true, trailing: true };

@Component({
  standalone: true,
  imports: [FreelancerAsyncPipeModule, NgTemplateOutlet, StickyModule],
  selector: 'fl-sticky-footer',
  template: `
    @if (position === StickyFooterPosition.FIXED) {
      <div
        class="StickyFooterContainer"
        [attr.data-full-width]="fullWidth"
        [attr.data-follow-keyboard]="followKeyboard"
        [flSticky]="true"
        [flStickyContext]="stickyContext"
        [flStickyStatic]="stickyStatic"
        [flStickyOffset]="stickyOffset"
        [flStickyPosition]="StickyPosition.BOTTOM"
        [flStickyBehaviour]="StickyBehaviour.ALWAYS"
      >
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </div>
    }

    @if (position === StickyFooterPosition.ABSOLUTE) {
      <ng-container *ngTemplateOutlet="content"></ng-container>
    }

    <ng-template #content>
      <div
        class="StickyFooter"
        [class.IsKeyboardOpen]="isKeyboardOpen$ | flAsync"
        [class.IsHidden]="(isKeyboardOpen$ | flAsync) && hideOnKeyboardActive"
        [attr.data-position]="position"
        [attr.data-box-shadow]="boxShadow"
        [attr.data-space-between]="spaceBetween"
        [attr.data-background-color]="backgroundColor"
        [attr.data-flex-align]="flexAlign"
        [attr.data-flex-justify]="flexJustify"
      >
        <ng-content></ng-content>
      </div>
    </ng-template>
  `,
  styleUrls: ['./sticky-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StickyFooterComponent implements OnChanges, OnDestroy, OnInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private keyboardService = inject(KeyboardService);
  private platformId = inject(PLATFORM_ID);

  StickyFooterPosition = StickyFooterPosition;
  StickyBehaviour = StickyBehaviour;
  StickyPosition = StickyPosition;

  isKeyboardOpen$: Observable<boolean>;

  /**
   * Height of the on-screen keyboard, so the footer can be lifted clear of it.
   * Combined with `--fl-keyboard-inset` in the stylesheet.
   */
  @HostBinding('style.--fl-sticky-footer-keyboard-inset.px')
  keyboardInset = 0;

  private keyboardSubscription?: Subscription;

  @Input() position = StickyFooterPosition.ABSOLUTE;
  @Input() boxShadow = true;
  @Input() stickyOffset = 0;
  @Input() stickyStatic = false;
  @Input() stickyContext: StickyContext = 'global';
  @Input() fullWidth = true;
  @Input() spaceBetween = false;
  @Input() flexAlign: FlexAlign = 'flex-end';
  @Input() flexJustify: FlexJustify = 'flex-end';

  // Hide when virtual keyboard is open
  // Note: This only for native apps
  @Input() hideOnKeyboardActive = true;
  @Input() backgroundColor = StickyFooterBackgroundColor.DEFAULT;

  /**
   * Lift the footer above the on-screen keyboard instead of leaving it at the
   * bottom of the viewport. Only applies to `StickyFooterPosition.FIXED`.
   *
   * Off by default because the native app resizes its webview when the keyboard
   * opens (`KeyboardResize.Native`), which already moves the viewport bottom to
   * the top of the keyboard. Turn it on for footers that must clear the keyboard
   * everywhere - mobile web, where the layout viewport does not shrink, and
   * pages using `useManualIosKeyboardAvoidance`, where the webview no longer
   * resizes.
   */
  @Input() followKeyboard = false;

  ngOnInit(): void {
    this.isKeyboardOpen$ = this.keyboardService.isOpen();
  }

  /**
   * `position` arrives asynchronously in most call sites, so keep the tracking
   * in step with it rather than deciding once: there is nothing to lift while
   * the footer is absolutely positioned, which is the desktop case.
   */
  ngOnChanges(): void {
    const shouldTrack =
      this.followKeyboard && this.position === StickyFooterPosition.FIXED;

    if (shouldTrack === isDefined(this.keyboardSubscription)) {
      return;
    }

    if (!shouldTrack) {
      this.keyboardSubscription?.unsubscribe();
      this.keyboardSubscription = undefined;
      this.keyboardInset = 0;
      return;
    }

    this.keyboardSubscription = this.keyboardInset$().subscribe(inset => {
      this.keyboardInset = inset;
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * The visual viewport shrinks by the height of the keyboard on mobile web, and
   * on iOS it is also offset when the page is scrolled to keep the focused field
   * visible. The gap it leaves at the bottom of the layout viewport is the space
   * we have to clear.
   */
  private keyboardInset$(): Observable<number> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(0);
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return of(0);
    }

    return merge(
      fromEvent(viewport, 'resize'),
      fromEvent(viewport, 'scroll'),
    ).pipe(
      throttleTime(KEYBOARD_INSET_THROTTLE_MS, undefined, THROTTLE_BOTH),
      map(() =>
        Math.max(
          0,
          /* eslint-disable-next-line no-restricted-globals, no-restricted-properties */
          window.innerHeight - viewport.height - viewport.offsetTop,
        ),
      ),
      distinctUntilChanged(),
      startWith(0),
    );
  }

  ngOnDestroy(): void {
    this.keyboardSubscription?.unsubscribe();
  }
}
